<template>
  <div class="reporte-comisiones py-4" :class="themeClass">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xxl-11 col-xl-12">

          <!-- ═══════════════════════════════════════════════════════ CABECERA -->
          <div class="card shadow-sm border-0 mb-4">
            <div class="card-header bg-degradado text-white d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div class="encabezado-texto">
                <h3 class="mb-0"><i class="bi bi-graph-up-arrow me-2"></i>Reporte de Comisiones</h3>
                <small class="subtitulo">Gestión, validación y pago de comisiones por vendedor</small>
              </div>
              <div class="panel-filtros d-flex gap-2 align-items-end flex-wrap">
                <div class="campo-filtro">
                  <label>Período</label>
                  <select class="form-select form-select-sm input-filtro" v-model="tipoPeriodo">
                    <option value="mensual">Mensual</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>
                <div class="campo-filtro" v-if="tipoPeriodo === 'mensual'">
                  <label>Mes</label>
                  <select class="form-select form-select-sm input-filtro" v-model.number="mesSeleccionado">
                    <option v-for="m in meses" :key="m.valor" :value="m.valor">{{ m.nombre }}</option>
                  </select>
                </div>
                <div class="campo-filtro" v-if="tipoPeriodo === 'trimestral'">
                  <label>Trimestre</label>
                  <select class="form-select form-select-sm input-filtro" v-model.number="trimestreSeleccionado">
                    <option :value="1">T1 (Ene–Mar)</option>
                    <option :value="2">T2 (Abr–Jun)</option>
                    <option :value="3">T3 (Jul–Sep)</option>
                    <option :value="4">T4 (Oct–Dic)</option>
                  </select>
                </div>
                <div class="campo-filtro">
                  <label>Año</label>
                  <select class="form-select form-select-sm input-filtro" v-model.number="anioSeleccionado">
                    <option v-for="a in aniosDisponibles" :key="a" :value="a">{{ a }}</option>
                  </select>
                </div>
                <div class="d-flex gap-2 pb-1">
                  <button class="btn btn-light btn-sm" @click="cargarReporte" :disabled="cargando">
                    <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-search me-1"></i>Consultar
                  </button>
                  <button class="btn btn-outline-light btn-sm" @click="exportarExcel" :disabled="!filas.length || exportando">
                    <i class="bi bi-file-earmark-excel me-1"></i>Excel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════════════ TARJETAS RESUMEN -->
          <div v-if="filas.length" class="row mb-4">
            <div class="col-6 col-lg-3 mb-3">
              <div class="tarjeta-kpi text-center">
                <div class="kpi-valor kpi-facturado">{{ formatMoney(totales.total_facturado) }}</div>
                <div class="kpi-label">Total facturado</div>
              </div>
            </div>
            <div class="col-6 col-lg-3 mb-3">
              <div class="tarjeta-kpi text-center">
                <div class="kpi-valor kpi-pagar">{{ formatMoney(totales.total_a_pagar) }}</div>
                <div class="kpi-label">Total a pagar</div>
              </div>
            </div>
            <div class="col-6 col-lg-3 mb-3">
              <div class="tarjeta-kpi text-center">
                <div class="kpi-valor kpi-neutro">{{ filas.length }}</div>
                <div class="kpi-label">Vendedores</div>
              </div>
            </div>
            <div class="col-6 col-lg-3 mb-3">
              <div class="tarjeta-kpi text-center">
                <div class="kpi-valor kpi-warn">{{ totales.meses_incumplidos }}</div>
                <div class="kpi-label">Meses sin cumplir meta</div>
              </div>
            </div>
          </div>

          <!-- ═════════════════════════════════════════════════════ TABLA PRINCIPAL -->
          <div class="card shadow-sm border-0">
            <div class="card-body p-0">
              <div v-if="cargando" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
                <p class="texto-suave mt-3">Cargando reporte…</p>
              </div>

              <div v-else-if="!filas.length && consultado" class="text-center py-5 texto-suave">
                <i class="bi bi-inbox display-4 d-block mb-3"></i>
                <p>No hay vendedores con configuración de comisión para este período</p>
              </div>

              <div v-else-if="filas.length" class="table-responsive">
                <table class="tabla-comisiones table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Vendedor</th>
                      <th class="text-center">Facturas</th>
                      <th class="text-end">Facturado</th>
                      <th style="min-width:110px">% Meta</th>
                      <th class="text-end">Sueldo</th>
                      <th class="text-end">Comisión</th>
                      <th class="text-end">Bono</th>
                      <th class="text-end fw-bold">Total a pagar</th>
                      <th v-if="!esPeriodoMensual">Detalle meses</th>
                      <th class="text-center" v-if="esPeriodoMensual">Estado</th>
                      <th class="text-center" v-if="esPeriodoMensual">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="fila in filas" :key="fila.usuario_id">
                      <td>
                        <div class="fw-semibold">{{ fila.nombre_vendedor }}</div>
                        <small class="texto-suave">Meta: {{ formatMoney(fila.meta_base_mensual) }}/mes</small>
                      </td>
                      <td class="text-center">
                        <span class="badge badge-facturas">{{ fila.numero_facturas ?? '—' }}</span>
                      </td>
                      <td class="text-end">{{ formatMoney(fila.total_facturado) }}</td>
                      <td>
                        <div v-if="fila.porcentaje_meta !== null">
                          <div class="d-flex justify-content-between small mb-1">
                            <span class="texto-suave">{{ fila.porcentaje_meta }}%</span>
                          </div>
                          <div class="barra-meta-fondo">
                            <div class="barra-meta-fill"
                                 :class="fila.porcentaje_meta >= 100 ? 'barra-ok' : fila.porcentaje_meta >= 70 ? 'barra-warn' : 'barra-danger'"
                                 :style="{ width: Math.min(fila.porcentaje_meta, 100) + '%' }">
                            </div>
                          </div>
                        </div>
                        <span v-else class="badge bg-secondary">Cerrado</span>
                      </td>
                      <td class="text-end">{{ formatMoney(fila.sueldo_calculado) }}</td>
                      <td class="text-end">{{ formatMoney(fila.comision_calculada) }}</td>
                      <td class="text-end">{{ formatMoney(fila.bono_venta) }}</td>
                      <td class="text-end fw-bold monto-total">{{ formatMoney(fila.total_a_pagar) }}</td>
                      <td v-if="!esPeriodoMensual">
                        <small class="texto-detalle">{{ formatearDetalleMensual(fila.detalle_mensual) }}</small>
                      </td>
                      <td class="text-center" v-if="esPeriodoMensual">
                        <span v-if="!fila.comision_id" class="badge bg-secondary">Pendiente</span>
                        <span v-else-if="fila.estado === 'borrador'" class="badge bg-warning text-dark">Borrador</span>
                        <span v-else-if="fila.estado === 'validado'" class="badge bg-success">Validado</span>
                        <span v-else-if="fila.estado === 'pagado'" class="badge bg-primary">Pagado</span>
                        <span v-else-if="fila.estado === 'aprobado'" class="badge bg-info text-dark">Aprobado</span>
                      </td>
                      <td class="text-center" v-if="esPeriodoMensual">
                        <div class="d-flex justify-content-center gap-1">
                          <button class="btn btn-sm btn-outline-primary" title="Ver detalle y facturas"
                                  @click="abrirDetalle(fila)" :disabled="procesando[fila.usuario_id]">
                            <i class="bi bi-eye"></i>
                          </button>
                          <button v-if="!fila.comision_id || fila.estado === 'borrador'"
                                  class="btn btn-sm btn-success" title="Validar y cerrar mes"
                                  @click="abrirValidar(fila)" :disabled="procesando[fila.usuario_id]">
                            <span v-if="procesando[fila.usuario_id]" class="spinner-border spinner-border-sm"></span>
                            <i v-else class="bi bi-patch-check"></i>
                          </button>
                          <button v-if="fila.estado === 'validado'"
                                  class="btn btn-sm btn-primary" title="Marcar como pagado"
                                  @click="marcarPagado(fila)" :disabled="procesando[fila.usuario_id]">
                            <i class="bi bi-cash-stack"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Totales</td>
                      <td class="text-center fw-bold">{{ totales.numero_facturas }}</td>
                      <td class="text-end fw-bold">{{ formatMoney(totales.total_facturado) }}</td>
                      <td></td>
                      <td class="text-end fw-bold">{{ formatMoney(totales.sueldo_calculado) }}</td>
                      <td class="text-end fw-bold">{{ formatMoney(totales.comision_calculada) }}</td>
                      <td class="text-end fw-bold">{{ formatMoney(totales.bono_venta) }}</td>
                      <td class="text-end fw-bold monto-total">{{ formatMoney(totales.total_a_pagar) }}</td>
                      <td v-if="!esPeriodoMensual"></td>
                      <td v-if="esPeriodoMensual"></td>
                      <td v-if="esPeriodoMensual"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════ MODAL DETALLE -->
  <Teleport to="body">
    <div v-if="detalleAbierto" class="modal-overlay" @click.self="cerrarDetalle">
      <div class="modal-detalle" :class="themeClass">

        <div class="modal-detalle-cabecera">
          <div>
            <h4 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Detalle de Comisión</h4>
            <p class="mb-0 texto-suave small">
              {{ detalleActual && detalleActual.nombre_vendedor }} &mdash;
              {{ nombreMesCompleto(mesSeleccionado) }} {{ anioSeleccionado }}
            </p>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="detalleActual && detalleActual.estado === 'validado'" class="badge bg-success">Validado</span>
            <span v-else-if="detalleActual && detalleActual.estado === 'pagado'" class="badge bg-primary">Pagado</span>
            <span v-else class="badge bg-secondary">Pendiente</span>
            <button class="btn btn-sm btn-outline-secondary" @click="cerrarDetalle"><i class="bi bi-x-lg"></i></button>
          </div>
        </div>

        <div v-if="cargandoDetalle" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="texto-suave mt-2">Cargando detalle…</p>
        </div>

        <div v-else-if="detalleActual" class="modal-detalle-cuerpo">

          <!-- FACTURAS -->
          <div class="seccion-detalle">
            <h6 class="seccion-titulo">
              <i class="bi bi-receipt me-2"></i>Facturas del período
              <span class="badge ms-2" :class="detalleActual.facturas.length ? 'bg-primary' : 'bg-secondary'">
                {{ detalleActual.facturas.length }}
              </span>
            </h6>
            <div v-if="!detalleActual.facturas.length" class="alert-info-suave">
              No se encontraron facturas autorizadas en este período.
            </div>
            <div v-else class="table-responsive tabla-facturas-wrap">
              <table class="table table-sm table-hover mb-0">
                <thead>
                  <tr>
                    <th>#</th><th>N° Factura</th><th>Fecha</th><th>Cliente</th>
                    <th>RUC / CI</th><th class="text-end">Subtotal</th>
                    <th class="text-end">IVA</th><th class="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(f, idx) in detalleActual.facturas" :key="f.id">
                    <td class="texto-suave">{{ idx + 1 }}</td>
                    <td class="font-monospace fw-semibold">{{ f.numero_factura }}</td>
                    <td>{{ formatFecha(f.fecha_emision) }}</td>
                    <td>{{ f.nombre_cliente || '—' }}</td>
                    <td class="texto-suave">{{ f.identificacion_cliente || '—' }}</td>
                    <td class="text-end">{{ formatMoney(f.subtotal) }}</td>
                    <td class="text-end texto-suave">{{ formatMoney(f.iva) }}</td>
                    <td class="text-end fw-semibold">{{ formatMoney(f.total) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="fw-bold">
                    <td colspan="5">TOTALES</td>
                    <td class="text-end">{{ formatMoney(sumaFacturas('subtotal')) }}</td>
                    <td class="text-end texto-suave">{{ formatMoney(sumaFacturas('iva')) }}</td>
                    <td class="text-end monto-total">{{ formatMoney(sumaFacturas('total')) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- RANGOS -->
          <div class="seccion-detalle">
            <h6 class="seccion-titulo"><i class="bi bi-sliders me-2"></i>Rangos de comisión configurados</h6>
            <div v-if="!detalleActual.config || !detalleActual.config.rangos_venta || !detalleActual.config.rangos_venta.length"
                 class="alert-info-suave">No hay rangos configurados.</div>
            <div v-else class="table-responsive">
              <table class="table table-sm mb-0">
                <thead>
                  <tr><th>Desde</th><th>Hasta</th><th class="text-center">% Comisión</th>
                      <th class="text-end">Bono adicional</th><th class="text-center">Aplica</th></tr>
                </thead>
                <tbody>
                  <tr v-for="rango in detalleActual.config.rangos_venta" :key="rango.id"
                      :class="esRangoAplicado(rango) ? 'fila-rango-activo' : ''">
                    <td>{{ formatMoney(rango.desde_monto) }}</td>
                    <td>{{ rango.hasta_monto !== null ? formatMoney(rango.hasta_monto) : '∞ Sin límite' }}</td>
                    <td class="text-center fw-semibold">{{ rango.porcentaje_comision }}%</td>
                    <td class="text-end">{{ formatMoney(rango.bono_adicional) }}</td>
                    <td class="text-center">
                      <i v-if="esRangoAplicado(rango)" class="bi bi-check-circle-fill text-success fs-5"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="!detalleActual.rango_aplicado" class="texto-suave small mt-2 mb-0">
              <i class="bi bi-exclamation-circle me-1"></i>
              El monto facturado ({{ formatMoney(detalleActual.total_facturado) }}) no cae en ningún rango definido.
            </p>
          </div>

          <!-- DESGLOSE DE PAGO -->
          <div class="seccion-detalle">
            <h6 class="seccion-titulo"><i class="bi bi-calculator me-2"></i>Desglose del pago</h6>
            <div class="desglose-pago">
              <div class="desglose-fila">
                <span>Sueldo base configurado</span>
                <span>{{ formatMoney(detalleActual.config && detalleActual.config.sueldo_base) }}</span>
              </div>
              <div class="desglose-fila texto-suave small">
                <span>Meta mensual</span>
                <span>{{ formatMoney(detalleActual.config && detalleActual.config.meta_base_mensual) }}</span>
              </div>
              <div class="desglose-fila texto-suave small">
                <span>Total facturado en el período</span>
                <span class="fw-semibold">{{ formatMoney(detalleActual.total_facturado) }}</span>
              </div>
              <div class="desglose-fila texto-suave small">
                <span>Cumplimiento de meta<span v-if="detalleActual.porcentaje_meta !== null"> ({{ detalleActual.porcentaje_meta }}%)</span></span>
                <span v-if="detalleActual.porcentaje_meta !== null && detalleActual.porcentaje_meta < 100" class="text-warning">Sueldo prorrateado</span>
                <span v-else class="text-success">Sueldo completo</span>
              </div>
              <div class="desglose-separador"></div>
              <div class="desglose-fila">
                <span>Sueldo calculado</span>
                <span>{{ formatMoney(detalleActual.sueldo_calculado) }}</span>
              </div>
              <div class="desglose-fila" v-if="detalleActual.rango_aplicado">
                <span>Comisión ({{ detalleActual.rango_aplicado.porcentaje_comision }}% de {{ formatMoney(detalleActual.total_facturado) }})</span>
                <span>{{ formatMoney(detalleActual.comision_calculada) }}</span>
              </div>
              <div class="desglose-fila" v-else>
                <span class="texto-suave">Comisión (sin rango aplicable)</span>
                <span class="texto-suave">{{ formatMoney(0) }}</span>
              </div>
              <div class="desglose-fila" v-if="Number(detalleActual.bono_venta) > 0">
                <span>Bono adicional de ventas</span>
                <span>{{ formatMoney(detalleActual.bono_venta) }}</span>
              </div>
              <div class="desglose-separador"></div>
              <div class="desglose-fila desglose-total">
                <span>TOTAL A PAGAR</span>
                <span>{{ formatMoney(detalleActual.total_a_pagar) }}</span>
              </div>
            </div>
          </div>

          <!-- INFO VALIDACIÓN -->
          <div v-if="detalleActual.registro && (detalleActual.estado === 'validado' || detalleActual.estado === 'pagado')"
               class="seccion-detalle seccion-validacion">
            <h6 class="seccion-titulo"><i class="bi bi-patch-check-fill me-2 text-success"></i>Información de validación</h6>
            <div class="info-validacion">
              <div class="info-fila">
                <span>Fuente de pago</span>
                <span class="fw-semibold">
                  <i :class="detalleActual.registro.fuente_pago === 'caja' ? 'bi bi-safe2' : 'bi bi-bank'" class="me-1"></i>
                  {{ detalleActual.registro.fuente_pago === 'caja'
                     ? 'Caja: ' + (detalleActual.registro.nombre_caja || detalleActual.registro.caja_id)
                     : 'Banco: ' + (detalleActual.registro.nombre_banco || '') + ' — ' + (detalleActual.registro.numero_cuenta_banco || '') }}
                </span>
              </div>
              <div class="info-fila">
                <span>Validado por</span>
                <span>{{ detalleActual.registro.nombre_validador || '—' }}</span>
              </div>
              <div class="info-fila">
                <span>Fecha de validación</span>
                <span>{{ formatFechaHora(detalleActual.registro.fecha_validacion) }}</span>
              </div>
            </div>
          </div>

        </div>

        <div v-if="detalleActual && !cargandoDetalle" class="modal-detalle-pie">
          <button class="btn btn-outline-secondary btn-sm" @click="imprimirComision">
            <i class="bi bi-printer me-1"></i>Imprimir / PDF
          </button>
          <div class="ms-auto d-flex gap-2">
            <button class="btn btn-secondary btn-sm" @click="cerrarDetalle">Cerrar</button>
            <button v-if="!detalleActual.comision_id || detalleActual.estado === 'borrador'"
                    class="btn btn-success btn-sm" @click="abrirValidarDesdeDetalle">
              <i class="bi bi-patch-check me-1"></i>Validar mes
            </button>
            <button v-if="detalleActual.estado === 'validado'"
                    class="btn btn-primary btn-sm" @click="marcarPagadoDesdeDetalle"
                    :disabled="procesando[detalleActual.usuario_id]">
              <i class="bi bi-cash-stack me-1"></i>Marcar pagado
            </button>
          </div>
        </div>

      </div>
    </div>
  </Teleport>

  <!-- ═══════════════════════════════════════════════════ MODAL VALIDAR -->
  <Teleport to="body">
    <div v-if="validarAbierto" class="modal-overlay modal-overlay-top" @click.self="cerrarValidar">
      <div class="modal-validar" :class="themeClass">
        <div class="modal-validar-cabecera">
          <h5 class="mb-0"><i class="bi bi-patch-check me-2"></i>Validar comisión</h5>
          <button class="btn btn-sm btn-outline-secondary" @click="cerrarValidar"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="modal-validar-cuerpo">
          <div class="alerta-cierre mb-4">
            <i class="bi bi-info-circle me-2"></i>
            <span>
              <strong>{{ filaValidar && filaValidar.nombre_vendedor }}</strong> &mdash;
              {{ nombreMesCompleto(mesSeleccionado) }} {{ anioSeleccionado }}.
              Al validar, los valores se registran definitivamente y el mes queda cerrado.
            </span>
          </div>

          <div class="resumen-validar mb-4">
            <div class="resumen-fila">
              <span>Total facturado</span>
              <span class="fw-bold">{{ formatMoney(filaValidar && filaValidar.total_facturado) }}</span>
            </div>
            <div class="resumen-fila">
              <span>Sueldo calculado</span>
              <span>{{ formatMoney(filaValidar && filaValidar.sueldo_calculado) }}</span>
            </div>
            <div class="resumen-fila" v-if="filaValidar && filaValidar.comision_calculada > 0">
              <span>Comisión</span>
              <span>{{ formatMoney(filaValidar.comision_calculada) }}</span>
            </div>
            <div class="resumen-fila" v-if="filaValidar && filaValidar.bono_venta > 0">
              <span>Bono ventas</span>
              <span>{{ formatMoney(filaValidar.bono_venta) }}</span>
            </div>
            <div class="resumen-fila resumen-total">
              <span>TOTAL A PAGAR</span>
              <span>{{ formatMoney(filaValidar && filaValidar.total_a_pagar) }}</span>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Fuente de pago</label>
            <div class="d-flex gap-3 mb-3">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="fuenteCaja" v-model="validarFuente" value="caja">
                <label class="form-check-label" for="fuenteCaja"><i class="bi bi-safe2 me-1"></i>Caja</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" id="fuenteBanco" v-model="validarFuente" value="banco">
                <label class="form-check-label" for="fuenteBanco"><i class="bi bi-bank me-1"></i>Banco</label>
              </div>
            </div>

            <div v-if="validarFuente === 'caja'">
              <label class="form-label small">Seleccionar caja</label>
              <div v-if="cargandoCuentas" class="texto-suave small">
                <span class="spinner-border spinner-border-sm me-1"></span>Cargando cajas…
              </div>
              <select v-else class="form-select" v-model.number="validarCajaId">
                <option :value="null" disabled>— Seleccione una caja —</option>
                <option v-for="c in cuentas.cajas" :key="c.id" :value="c.id">{{ c.nombre }}</option>
              </select>
              <p v-if="!cuentas.cajas.length && !cargandoCuentas" class="text-warning small mt-1">
                No hay cajas activas registradas.
              </p>
            </div>

            <div v-if="validarFuente === 'banco'">
              <label class="form-label small">Seleccionar cuenta bancaria</label>
              <div v-if="cargandoCuentas" class="texto-suave small">
                <span class="spinner-border spinner-border-sm me-1"></span>Cargando bancos…
              </div>
              <select v-else class="form-select" v-model.number="validarBancoId">
                <option :value="null" disabled>— Seleccione una cuenta —</option>
                <option v-for="b in cuentas.bancos" :key="b.id" :value="b.id">
                  {{ b.nombre }} — {{ b.numero_cuenta }} ({{ b.tipo_cuenta }})
                </option>
              </select>
              <p v-if="!cuentas.bancos.length && !cargandoCuentas" class="text-warning small mt-1">
                No hay cuentas bancarias activas registradas.
              </p>
            </div>
          </div>
        </div>

        <div class="modal-validar-pie">
          <button class="btn btn-secondary btn-sm" @click="cerrarValidar" :disabled="procesandoValidar">Cancelar</button>
          <button class="btn btn-success btn-sm" @click="confirmarValidacion"
                  :disabled="procesandoValidar || !puedeConfirmarValidar">
            <span v-if="procesandoValidar" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-patch-check me-1"></i>Confirmar validación
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import * as XLSX from 'xlsx';
import themeManager from '@/store/theme';
import { comisionesService } from '@/services/comisionesService';
import notificationService from '@/services/notificationService';

export default {
  name: 'ClientReporteComisiones',

  computed: {
    themeClass() { return themeManager.themeClass; },
    esPeriodoMensual() { return this.tipoPeriodo === 'mensual'; },
    totales() {
      const sum = (c) => this.filas.reduce((a, f) => a + (Number(f[c]) || 0), 0);
      return {
        total_facturado:    sum('total_facturado'),
        numero_facturas:    this.filas.reduce((a, f) => a + (Number(f.numero_facturas) || 0), 0),
        sueldo_calculado:   sum('sueldo_calculado'),
        comision_calculada: sum('comision_calculada'),
        bono_venta:         sum('bono_venta'),
        total_a_pagar:      sum('total_a_pagar'),
        meses_incumplidos:  this.filas.reduce((a, f) => a + (Number(f.meses_incumplidos) || 0), 0),
      };
    },
    puedeConfirmarValidar() {
      if (!this.validarFuente) return false;
      if (this.validarFuente === 'caja'  && !this.validarCajaId)  return false;
      if (this.validarFuente === 'banco' && !this.validarBancoId) return false;
      return true;
    },
  },

  data() {
    const hoy = new Date();
    const anioActual = hoy.getFullYear();
    return {
      cargando: false, exportando: false, consultado: false, procesando: {},
      tipoPeriodo: 'mensual',
      mesSeleccionado: hoy.getMonth() + 1,
      trimestreSeleccionado: Math.floor(hoy.getMonth() / 3) + 1,
      anioSeleccionado: anioActual,
      filas: [],
      temporizadorActualizacion: null,
      detalleAbierto: false, cargandoDetalle: false, detalleActual: null,
      validarAbierto: false, filaValidar: null,
      validarFuente: null, validarCajaId: null, validarBancoId: null,
      procesandoValidar: false, cargandoCuentas: false,
      cuentas: { cajas: [], bancos: [] },
      meses: [
        { valor:  1, nombre: 'Ene', completo: 'Enero'      },
        { valor:  2, nombre: 'Feb', completo: 'Febrero'    },
        { valor:  3, nombre: 'Mar', completo: 'Marzo'      },
        { valor:  4, nombre: 'Abr', completo: 'Abril'      },
        { valor:  5, nombre: 'May', completo: 'Mayo'       },
        { valor:  6, nombre: 'Jun', completo: 'Junio'      },
        { valor:  7, nombre: 'Jul', completo: 'Julio'      },
        { valor:  8, nombre: 'Ago', completo: 'Agosto'     },
        { valor:  9, nombre: 'Sep', completo: 'Septiembre' },
        { valor: 10, nombre: 'Oct', completo: 'Octubre'    },
        { valor: 11, nombre: 'Nov', completo: 'Noviembre'  },
        { valor: 12, nombre: 'Dic', completo: 'Diciembre'  },
      ],
      aniosDisponibles: Array.from({ length: 5 }, (_, i) => anioActual - i),
    };
  },

  async mounted() {
    await this.cargarReporte();
    this.iniciarActualizacionAutomatica();
  },
  beforeUnmount() { this.detenerActualizacionAutomatica(); },

  methods: {
    iniciarActualizacionAutomatica() {
      this.detenerActualizacionAutomatica();
      this.temporizadorActualizacion = setInterval(() => {
        if (this.cargando || this.detalleAbierto || this.validarAbierto) return;
        if (Object.values(this.procesando).some(Boolean)) return;
        this.cargarReporteSilencioso();
      }, 20000);
    },
    detenerActualizacionAutomatica() {
      if (this.temporizadorActualizacion) {
        clearInterval(this.temporizadorActualizacion);
        this.temporizadorActualizacion = null;
      }
    },
    async cargarReporteSilencioso() {
      try { this.filas = await this.obtenerFilasPorPeriodo(); this.consultado = true; } catch { /* silencioso */ }
    },
    async cargarReporte() {
      this.cargando = true; this.consultado = false;
      try { this.filas = await this.obtenerFilasPorPeriodo(); this.consultado = true; }
      catch { notificationService.error('Error al cargar el reporte de comisiones'); }
      finally { this.cargando = false; }
    },
    obtenerMesesPeriodo() {
      if (this.tipoPeriodo === 'mensual')    return [this.mesSeleccionado];
      if (this.tipoPeriodo === 'trimestral') {
        const inicio = (this.trimestreSeleccionado - 1) * 3 + 1;
        return [inicio, inicio + 1, inicio + 2];
      }
      return this.meses.map(m => m.valor);
    },
    async obtenerFilasPorPeriodo() {
      const mesesLista = this.obtenerMesesPeriodo();
      const respuestas = await Promise.all(
        mesesLista.map(mes => comisionesService.getReportePeriodo(this.anioSeleccionado, mes))
      );
      return this.unificarFilasPorPeriodo(
        respuestas.map((r, i) => ({ mes: mesesLista[i], filas: r.data || [] }))
      );
    },
    unificarFilasPorPeriodo(bloques) {
      const ag = new Map();
      for (const bloque of bloques) {
        for (const fila of bloque.filas) {
          const uid = Number(fila.usuario_id) || 0;
          if (!uid) continue;
          if (!ag.has(uid)) {
            ag.set(uid, {
              usuario_id: uid, nombre_vendedor: fila.nombre_vendedor,
              meta_base_mensual: Number(fila.meta_base_mensual) || 0,
              sueldo_base: Number(fila.sueldo_base) || 0,
              total_facturado: 0, numero_facturas: 0,
              sueldo_calculado: 0, comision_calculada: 0,
              bono_venta: 0, total_a_pagar: 0,
              detalle_mensual: [], meses_incumplidos: 0,
              estado: null, comision_id: null, porcentaje_meta: null,
            });
          }
          const item = ag.get(uid);
          const meta  = Number(fila.meta_base_mensual) || 0;
          const factu = Number(fila.total_facturado)   || 0;
          item.total_facturado    += factu;
          item.numero_facturas    += Number(fila.numero_facturas)    || 0;
          item.sueldo_calculado   += Number(fila.sueldo_calculado)   || 0;
          item.comision_calculada += Number(fila.comision_calculada) || 0;
          item.bono_venta         += Number(fila.bono_venta)         || 0;
          item.total_a_pagar      += Number(fila.total_a_pagar)      || 0;
          item.detalle_mensual.push({ mes: bloque.mes, total_facturado: factu, total_a_pagar: Number(fila.total_a_pagar) || 0 });
          if (meta > 0 && factu < meta) item.meses_incumplidos += 1;
          if (this.esPeriodoMensual) {
            item.estado      = fila.estado      ?? null;
            item.comision_id = fila.comision_id ?? null;
            item.porcentaje_meta = fila.porcentaje_meta ?? null;
          }
        }
      }
      return Array.from(ag.values()).sort((a, b) => (a.nombre_vendedor || '').localeCompare(b.nombre_vendedor || ''));
    },

    // ─── MODAL DETALLE ────────────────────────────────────────────────────────
    async abrirDetalle(fila) {
      this.detalleAbierto = true; this.cargandoDetalle = true; this.detalleActual = null;
      try {
        const res = await comisionesService.getDetalle(fila.usuario_id, this.anioSeleccionado, this.mesSeleccionado);
        this.detalleActual = res.data || null;
      } catch { notificationService.error('Error al cargar el detalle'); this.detalleAbierto = false; }
      finally { this.cargandoDetalle = false; }
    },
    cerrarDetalle() { this.detalleAbierto = false; this.detalleActual = null; },
    esRangoAplicado(rango) {
      const a = this.detalleActual && this.detalleActual.rango_aplicado;
      return a ? String(a.id) === String(rango.id) : false;
    },
    sumaFacturas(campo) {
      return ((this.detalleActual && this.detalleActual.facturas) || []).reduce((a, f) => a + (Number(f[campo]) || 0), 0);
    },
    abrirValidarDesdeDetalle() {
      const fila = this.filas.find(f => f.usuario_id === (this.detalleActual && this.detalleActual.usuario_id));
      this.cerrarDetalle();
      if (fila) this.abrirValidar(fila);
    },
    async marcarPagadoDesdeDetalle() {
      const fila = this.filas.find(f => f.usuario_id === (this.detalleActual && this.detalleActual.usuario_id));
      this.cerrarDetalle();
      if (fila) await this.marcarPagado(fila);
    },

    // ─── MODAL VALIDAR ────────────────────────────────────────────────────────
    async abrirValidar(fila) {
      this.filaValidar = fila; this.validarFuente = null;
      this.validarCajaId = null; this.validarBancoId = null;
      this.validarAbierto = true;
      await this.cargarCuentas();
    },
    cerrarValidar() { this.validarAbierto = false; this.filaValidar = null; },
    async cargarCuentas() {
      if (this.cuentas.cajas.length || this.cuentas.bancos.length) return;
      this.cargandoCuentas = true;
      try {
        const res = await comisionesService.getCuentas();
        this.cuentas = (res && res.data) || { cajas: [], bancos: [] };
      } catch { notificationService.error('Error al cargar cuentas'); }
      finally { this.cargandoCuentas = false; }
    },
    async confirmarValidacion() {
      if (!this.puedeConfirmarValidar || !this.filaValidar) return;
      this.procesandoValidar = true;
      try {
        await comisionesService.validar(
          this.filaValidar.usuario_id, this.anioSeleccionado, this.mesSeleccionado,
          this.validarFuente,
          this.validarFuente === 'caja'  ? this.validarCajaId  : null,
          this.validarFuente === 'banco' ? this.validarBancoId : null,
        );
        notificationService.success('Comisión validada y mes cerrado correctamente');
        this.cerrarValidar();
        await this.cargarReporteSilencioso();
      } catch (e) {
        notificationService.error((e.response && e.response.data && e.response.data.error) || 'Error al validar la comisión');
      } finally { this.procesandoValidar = false; }
    },

    // ─── MARCAR PAGADO ────────────────────────────────────────────────────────
    async marcarPagado(fila) {
      if (!fila.comision_id) return;
      this.procesando = { ...this.procesando, [fila.usuario_id]: true };
      try {
        await comisionesService.cambiarEstado(fila.comision_id, 'pagado');
        notificationService.success('Comisión marcada como pagada');
        await this.cargarReporteSilencioso();
      } catch (e) {
        notificationService.error((e.response && e.response.data && e.response.data.error) || 'Error al marcar como pagada');
      } finally { this.procesando = { ...this.procesando, [fila.usuario_id]: false }; }
    },

    // ─── IMPRESIÓN ────────────────────────────────────────────────────────────
    imprimirComision() {
      const d = this.detalleActual;
      if (!d) return;
      const mesNombre = this.nombreMesCompleto(d.mes || this.mesSeleccionado);
      const html = this.generarHtmlImprimible(d, mesNombre, this.anioSeleccionado);
      const ventana = window.open('', '_blank', 'width=900,height=700');
      ventana.document.write(html);
      ventana.document.close();
      ventana.focus();
      setTimeout(() => ventana.print(), 600);
    },

    generarHtmlImprimible(d, mesNombre, anio) {
      const fmt  = (v) => '$' + (Number(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const fmtF = (v) => v ? String(v).split('T')[0] : '—';

      const rangos = ((d.config && d.config.rangos_venta) || []).map(r => {
        const esAplicado = d.rango_aplicado && String(r.id) === String(d.rango_aplicado.id);
        return `<tr style="${esAplicado ? 'background:#d1fae5;font-weight:bold' : ''}">
          <td>${fmt(r.desde_monto)}</td>
          <td>${r.hasta_monto !== null ? fmt(r.hasta_monto) : '∞'}</td>
          <td style="text-align:center">${r.porcentaje_comision}%</td>
          <td style="text-align:right">${fmt(r.bono_adicional)}</td>
          <td style="text-align:center">${esAplicado ? '&#10003;' : ''}</td>
        </tr>`;
      }).join('');

      const factFilas = (d.facturas || []).map((f, i) => `
        <tr>
          <td>${i + 1}</td>
          <td style="font-family:monospace">${f.numero_factura || '—'}</td>
          <td>${fmtF(f.fecha_emision)}</td>
          <td>${f.nombre_cliente || '—'}</td>
          <td>${f.identificacion_cliente || '—'}</td>
          <td style="text-align:right">${fmt(f.subtotal)}</td>
          <td style="text-align:right">${fmt(f.iva)}</td>
          <td style="text-align:right;font-weight:bold">${fmt(f.total)}</td>
        </tr>`).join('');

      const sumSub   = (d.facturas || []).reduce((a, f) => a + (Number(f.subtotal) || 0), 0);
      const sumIva   = (d.facturas || []).reduce((a, f) => a + (Number(f.iva)      || 0), 0);
      const sumTotal = (d.facturas || []).reduce((a, f) => a + (Number(f.total)    || 0), 0);

      const pagoInfo = (d.registro && (d.estado === 'validado' || d.estado === 'pagado')) ? `
        <div style="margin-top:16px;padding:12px;border:1px solid #a7f3d0;border-radius:8px;background:#f0fdf4">
          <strong>&#10003; Comisi&oacute;n validada</strong><br>
          Fuente de pago: ${d.registro.fuente_pago === 'caja'
            ? 'Caja &mdash; ' + (d.registro.nombre_caja || d.registro.caja_id)
            : 'Banco &mdash; ' + (d.registro.nombre_banco || '') + ' ' + (d.registro.numero_cuenta_banco || '')}<br>
          Validado por: ${d.registro.nombre_validador || '&mdash;'}<br>
          Fecha: ${fmtF(d.registro.fecha_validacion)}<br>
          Estado: ${(d.estado || '').toUpperCase()}
        </div>` : '';

      return `<!DOCTYPE html><html lang="es"><head>
        <meta charset="UTF-8">
        <title>Comisi&oacute;n ${d.nombre_vendedor} ${mesNombre} ${anio}</title>
        <style>
          body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;margin:24px}
          h1{font-size:18px;margin:0}
          h2{font-size:13px;color:#0369a1;border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin-top:20px}
          table{width:100%;border-collapse:collapse;margin-top:8px;font-size:11px}
          th{background:#f1f5f9;padding:5px 8px;text-align:left}
          td{padding:4px 8px;border-bottom:1px solid #f1f5f9}
          .desglose{max-width:420px}
          .desglose td:first-child{color:#64748b}
          .desglose td:last-child{text-align:right;font-weight:600}
          .total-row td{font-size:13px;font-weight:bold;color:#059669;border-top:2px solid #e2e8f0}
          .firmas{margin-top:50px;display:flex;justify-content:space-around}
          .firma{text-align:center}
          .firma-linea{border-top:1px solid #334155;width:200px;margin:0 auto 4px}
          @media print{body{margin:10px}}
        </style>
      </head><body>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0369a1;padding-bottom:12px;margin-bottom:16px">
          <div>
            <h1>COMPROBANTE DE COMISI&Oacute;N</h1>
            <p style="margin:2px 0;color:#64748b">Sistema SICECOM ERP</p>
          </div>
          <div style="text-align:right">
            <p style="margin:2px 0"><strong>Vendedor:</strong> ${d.nombre_vendedor}</p>
            <p style="margin:2px 0"><strong>Per&iacute;odo:</strong> ${mesNombre} ${anio}</p>
            <p style="margin:2px 0"><strong>Generado:</strong> ${new Date().toLocaleDateString('es-EC')}</p>
          </div>
        </div>
        <h2>Facturas del per&iacute;odo (${(d.facturas || []).length} facturas)</h2>
        <table>
          <thead><tr><th>#</th><th>N&deg; Factura</th><th>Fecha</th><th>Cliente</th><th>RUC/CI</th><th>Subtotal</th><th>IVA</th><th>Total</th></tr></thead>
          <tbody>${factFilas || '<tr><td colspan="8" style="text-align:center;color:#94a3b8">Sin facturas autorizadas</td></tr>'}</tbody>
          <tfoot><tr style="font-weight:bold;background:#f8fafc">
            <td colspan="5">TOTALES</td>
            <td style="text-align:right">${fmt(sumSub)}</td>
            <td style="text-align:right">${fmt(sumIva)}</td>
            <td style="text-align:right">${fmt(sumTotal)}</td>
          </tr></tfoot>
        </table>
        <h2>Rangos de comisi&oacute;n configurados</h2>
        <table>
          <thead><tr><th>Desde</th><th>Hasta</th><th>% Comisi&oacute;n</th><th>Bono adicional</th><th>Aplica</th></tr></thead>
          <tbody>${rangos || '<tr><td colspan="5" style="text-align:center;color:#94a3b8">Sin rangos</td></tr>'}</tbody>
        </table>
        <h2>Desglose del pago</h2>
        <table class="desglose">
          <tr><td>Sueldo base</td><td>${fmt(d.config && d.config.sueldo_base)}</td></tr>
          <tr><td>Meta mensual</td><td>${fmt(d.config && d.config.meta_base_mensual)}</td></tr>
          <tr><td>Total facturado</td><td>${fmt(d.total_facturado)}</td></tr>
          <tr><td>Cumplimiento de meta</td><td>${d.porcentaje_meta !== null && d.porcentaje_meta !== undefined ? d.porcentaje_meta + '%' : 'N/A'}</td></tr>
          <tr><td></td><td></td></tr>
          <tr><td>Sueldo calculado</td><td>${fmt(d.sueldo_calculado)}</td></tr>
          <tr><td>Comisi&oacute;n ${d.rango_aplicado ? '(' + d.rango_aplicado.porcentaje_comision + '%)' : '(sin rango)'}</td><td>${fmt(d.comision_calculada)}</td></tr>
          <tr><td>Bono adicional</td><td>${fmt(d.bono_venta)}</td></tr>
          <tr class="total-row"><td>TOTAL A PAGAR</td><td>${fmt(d.total_a_pagar)}</td></tr>
        </table>
        ${pagoInfo}
        <div class="firmas">
          <div class="firma">
            <div class="firma-linea"></div>
            <p>Representante de la empresa</p>
          </div>
          <div class="firma">
            <div class="firma-linea"></div>
            <p>${d.nombre_vendedor}</p>
            <p style="color:#64748b;font-size:10px">Vendedor &mdash; Firma de conformidad</p>
          </div>
        </div>
      </body></html>`;
    },

    // ─── EXCEL ────────────────────────────────────────────────────────────────
    exportarExcel() {
      this.exportando = true;
      try {
        const cabecera = ['Vendedor','N° Facturas','Total Facturado','% Meta','Sueldo','Comisión','Bono Ventas','Total a Pagar','Detalle Meses','Estado'];
        const filas = this.filas.map(f => [
          f.nombre_vendedor,
          Number(f.numero_facturas)    || 0,
          Number(f.total_facturado)    || 0,
          f.porcentaje_meta !== null ? f.porcentaje_meta + '%' : '—',
          Number(f.sueldo_calculado)   || 0,
          Number(f.comision_calculada) || 0,
          Number(f.bono_venta)         || 0,
          Number(f.total_a_pagar)      || 0,
          this.formatearDetalleMensual(f.detalle_mensual),
          f.estado || 'Pendiente',
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([cabecera, ...filas]), 'Comisiones');
        XLSX.writeFile(wb, `comisiones_${this.esPeriodoMensual ? this.nombreMesCompleto(this.mesSeleccionado) : this.tipoPeriodo}_${this.anioSeleccionado}.xlsx`);
      } finally { this.exportando = false; }
    },

    // ─── FORMATEO ─────────────────────────────────────────────────────────────
    formatMoney(val) {
      return '$' + (Number(val) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatFecha(val) { return val ? String(val).split('T')[0] : '—'; },
    formatFechaHora(val) { return val ? String(val).replace('T', ' ').slice(0, 19) : '—'; },
    nombreMes(num) { return (this.meses.find(m => m.valor === num) || {}).nombre || String(num); },
    nombreMesCompleto(num) { return (this.meses.find(m => m.valor === num) || {}).completo || String(num); },
    formatearDetalleMensual(d) {
      if (!Array.isArray(d) || !d.length) return '—';
      return d.map(x => `${this.nombreMes(x.mes)}: ${this.formatMoney(x.total_facturado)}`).join(' | ');
    },
  },
};
</script>

<style scoped>
.reporte-comisiones {
  background-color: var(--bg-main, #f8fafc);
  color: var(--text-primary, #0f172a);
  min-height: 100vh;
  padding-bottom: 2rem;
}
.card {
  background-color: var(--secondary-bg-main, #ffffff);
  border-color: var(--border-main, #e2e8f0) !important;
  color: var(--text-primary, #0f172a);
}
.bg-degradado {
  background: linear-gradient(135deg, var(--primary-main, #0ea5e9) 0%, var(--primary-hover, #0284c7) 100%);
}
.encabezado-texto { flex: 1 1 360px; }
.subtitulo { color: rgb(255 255 255 / 0.88); }
.panel-filtros {
  background-color: rgb(255 255 255 / 0.14);
  border: 1px solid rgb(255 255 255 / 0.26);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
}
.campo-filtro { min-width: 106px; }
.campo-filtro label {
  display: block; font-size: 0.72rem; font-weight: 600;
  color: rgb(255 255 255 / 0.94); margin-bottom: 0.2rem;
}
.input-filtro {
  min-width: 102px;
  background-color: var(--secondary-bg-main, #fff);
  color: var(--text-primary, #0f172a);
  border-color: var(--border-main, #e2e8f0);
}
.tarjeta-kpi {
  background-color: var(--secondary-bg-main, #ffffff);
  border-radius: 12px; padding: 1rem;
  box-shadow: var(--shadow-sm, 0 1px 2px 0 rgb(0 0 0 / 0.05));
}
.kpi-valor { font-size: 1.4rem; font-weight: 700; }
.kpi-label { font-size: 0.78rem; color: var(--text-secondary, #475569); margin-top: 2px; }
.kpi-facturado { color: var(--primary-main,    #0ea5e9); }
.kpi-pagar     { color: var(--accent-success,  #10b981); }
.kpi-neutro    { color: var(--text-secondary,  #475569); }
.kpi-warn      { color: var(--accent-warning,  #f59e0b); }
.tabla-comisiones { color: var(--text-primary, #0f172a); }
.tabla-comisiones thead th {
  border-color: var(--border-main, #e2e8f0);
  background-color: color-mix(in srgb, var(--secondary-bg-main, #fff) 85%, var(--bg-main, #f8fafc) 15%);
  font-size: 0.82rem; font-weight: 600;
}
.tabla-comisiones tbody tr:hover {
  background-color: color-mix(in srgb, var(--primary-main, #0ea5e9) 7%, transparent 93%);
}
.tabla-comisiones tfoot td {
  background-color: color-mix(in srgb, var(--secondary-bg-main, #fff) 85%, var(--bg-main, #f8fafc) 15%);
  border-top: 2px solid var(--border-main, #e2e8f0);
}
.monto-total   { color: var(--accent-success, #10b981); }
.texto-suave   { color: var(--text-muted, #94a3b8); }
.texto-detalle { color: var(--text-secondary, #475569); font-size: 0.8rem; }
.badge-facturas {
  background-color: color-mix(in srgb, var(--primary-main, #0ea5e9) 15%, var(--secondary-bg-main, #fff) 85%);
  color: var(--primary-main, #0ea5e9);
  border: 1px solid color-mix(in srgb, var(--primary-main, #0ea5e9) 30%, transparent 70%);
}
.barra-meta-fondo {
  height: 6px; border-radius: 3px;
  background-color: var(--border-main, #e2e8f0); overflow: hidden;
}
.barra-meta-fill { height: 100%; border-radius: 3px; transition: width 0.3s ease; }
.barra-ok     { background-color: var(--accent-success, #10b981); }
.barra-warn   { background-color: var(--accent-warning, #f59e0b); }
.barra-danger { background-color: var(--accent-error,   #ef4444); }
.modal-overlay {
  position: fixed; inset: 0; background: rgb(0 0 0 / 0.55);
  z-index: 1060; display: flex; align-items: center;
  justify-content: center; padding: 1rem; overflow-y: auto;
}
.modal-overlay-top { align-items: flex-start; padding-top: 3rem; }
.modal-detalle {
  background-color: var(--secondary-bg-main, #fff);
  border-radius: 16px; width: 100%; max-width: 900px;
  max-height: 88vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgb(0 0 0 / 0.3);
  color: var(--text-primary, #0f172a);
}
.modal-detalle-cabecera {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-main, #e2e8f0); gap: 1rem;
}
.modal-detalle-cuerpo { flex: 1; overflow-y: auto; padding: 1.25rem 1.5rem; }
.modal-detalle-pie {
  display: flex; align-items: center;
  padding: 1rem 1.5rem; border-top: 1px solid var(--border-main, #e2e8f0); gap: 0.5rem;
}
.seccion-detalle {
  margin-bottom: 1.5rem; padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-main, #e2e8f0);
}
.seccion-detalle:last-child { border-bottom: none; }
.seccion-titulo {
  font-size: 0.88rem; font-weight: 700;
  color: var(--text-secondary, #475569);
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.75rem;
}
.tabla-facturas-wrap { max-height: 280px; overflow-y: auto; }
.alert-info-suave {
  padding: 0.6rem 0.9rem; border-radius: 8px; font-size: 0.85rem;
  color: var(--text-secondary, #475569);
  background-color: color-mix(in srgb, var(--primary-main, #0ea5e9) 10%, var(--secondary-bg-main, #fff) 90%);
}
.fila-rango-activo {
  background-color: color-mix(in srgb, var(--accent-success, #10b981) 15%, var(--secondary-bg-main, #fff) 85%) !important;
  font-weight: 600;
}
.desglose-pago {
  background-color: color-mix(in srgb, var(--bg-main, #f8fafc) 60%, var(--secondary-bg-main, #fff) 40%);
  border-radius: 12px; padding: 1rem 1.25rem; max-width: 460px;
}
.desglose-fila {
  display: flex; justify-content: space-between; padding: 0.3rem 0; font-size: 0.88rem;
}
.desglose-separador { border-top: 1px solid var(--border-main, #e2e8f0); margin: 0.4rem 0; }
.desglose-total {
  font-size: 1rem; font-weight: 700;
  color: var(--accent-success, #10b981); padding-top: 0.5rem;
}
.seccion-validacion {
  background-color: color-mix(in srgb, var(--accent-success, #10b981) 8%, var(--secondary-bg-main, #fff) 92%);
  border-radius: 12px; padding: 1rem 1.25rem;
}
.info-validacion .info-fila {
  display: flex; justify-content: space-between; padding: 0.25rem 0; font-size: 0.88rem;
}
.info-validacion .info-fila span:first-child { color: var(--text-secondary, #475569); }
.modal-validar {
  background-color: var(--secondary-bg-main, #fff);
  border-radius: 16px; width: 100%; max-width: 480px;
  box-shadow: 0 20px 60px rgb(0 0 0 / 0.3);
  color: var(--text-primary, #0f172a);
}
.modal-validar-cabecera {
  display: flex; justify-content: space-between; align-items: center;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid var(--border-main, #e2e8f0);
}
.modal-validar-cuerpo { padding: 1.25rem 1.5rem; }
.modal-validar-pie {
  display: flex; justify-content: flex-end; gap: 0.5rem;
  padding: 1rem 1.5rem; border-top: 1px solid var(--border-main, #e2e8f0);
}
.alerta-cierre {
  background-color: color-mix(in srgb, var(--accent-warning, #f59e0b) 12%, var(--secondary-bg-main, #fff) 88%);
  border: 1px solid color-mix(in srgb, var(--accent-warning, #f59e0b) 30%, transparent 70%);
  border-radius: 8px; padding: 0.75rem 1rem; font-size: 0.88rem;
  display: flex; align-items: flex-start; gap: 0.5rem;
}
.resumen-validar {
  background-color: color-mix(in srgb, var(--bg-main, #f8fafc) 60%, var(--secondary-bg-main, #fff) 40%);
  border-radius: 10px; padding: 0.75rem 1rem;
}
.resumen-fila {
  display: flex; justify-content: space-between; padding: 0.2rem 0; font-size: 0.88rem;
}
.resumen-total {
  font-weight: 700; font-size: 1rem; color: var(--accent-success, #10b981);
  border-top: 1px solid var(--border-main, #e2e8f0); padding-top: 0.4rem; margin-top: 0.2rem;
}
.form-select {
  background-color: var(--secondary-bg-main, #fff);
  color: var(--text-primary, #0f172a);
  border-color: var(--border-main, #e2e8f0);
}
.form-select:focus {
  border-color: var(--border-focus, #0ea5e9);
  box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--primary-main, #0ea5e9) 24%, transparent 76%);
}
.table { color: var(--text-primary, #0f172a); }
.table thead th {
  background-color: color-mix(in srgb, var(--secondary-bg-main, #fff) 85%, var(--bg-main, #f8fafc) 15%);
  border-color: var(--border-main, #e2e8f0);
}
.dark-theme .panel-filtros {
  background-color: rgb(2 6 23 / 0.28);
  border-color: rgb(148 163 184 / 0.32);
}
.dark-theme .modal-detalle,
.dark-theme .modal-validar { background-color: var(--secondary-bg-main, #1e293b); }
.dark-theme .fila-rango-activo {
  background-color: color-mix(in srgb, var(--accent-success, #10b981) 20%, var(--secondary-bg-main, #1e293b) 80%) !important;
}
</style>
